import { useState } from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../ui/accordion.tsx";

import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select.tsx";

import { Input } from "../../ui/input.tsx";
import { Label } from "../../ui/label.tsx";
import { Switch } from "../../ui/switch.tsx";
import { Button } from "../../ui/button.tsx";

import { useApi } from "../../../Api/useApi.ts";

import { Separator } from "../../ui/separator.tsx";
import { useMutation } from "@tanstack/react-query";
import { PackageSearch, Trash, Save } from "lucide-react";

import { WebhookCreateType } from "../../../Api/types.ts";
import { useFormEditorContext } from "../../../Contexts/FormEditorContext.jsx";

type WebhookItemProps = {
    webhook: {
        id: string;
        url: string;
        event: string;
        event_filters: string[];
        status: string;
    };
    setOpenDeleteDialog: (open: boolean) => void;
    setSelectedWebhookId: (id: string) => void;
    setOpenDeliveries: (open: boolean) => void;
    setSelectedWebhookUrl: (url: string) => void;
};

export const WebhookItem = ({
    webhook,
    setOpenDeleteDialog,
    setSelectedWebhookId,
    setOpenDeliveries,
    setSelectedWebhookUrl,
}: WebhookItemProps) => {
    const api = useApi();
    const { setFormSettings } = useFormEditorContext();
    const [localWebhook, setLocalWebhook] = useState(webhook);

    const handleDeleteWebhook = (webhookId: string) => {
        setSelectedWebhookId(webhookId);
        setOpenDeleteDialog(true);
    };

    const handleOpenDeliveries = (webhookId: string, webhookUrl: string) => {
        setSelectedWebhookId(webhookId);
        setSelectedWebhookUrl(webhookUrl);
        setOpenDeliveries(true);
    };
    const updateWebhookMutation = useMutation({
        mutationFn: ({
            webhookId,
            data,
        }: {
            webhookId: string;
            data: WebhookCreateType;
        }) => api.integrations.updateWebhook(webhookId, data),
        onSuccess: (data) => {
            setFormSettings((prev) => {
                if (!prev) return prev;
                const updatedWebhook = data?.data;
                const updatedIntegrations =
                    prev.integrations?.webhook_integration?.map((wh) =>
                        wh.id === updatedWebhook.id
                            ? {
                                  ...wh,
                                  url: updatedWebhook.url,
                                  event: updatedWebhook.event,
                                  event_filters: updatedWebhook.event_filters,
                                  status: updatedWebhook.status,
                              }
                            : wh,
                    ) || [];
                return {
                    ...prev,
                    integrations: {
                        ...prev.integrations,
                        webhook_integration: updatedIntegrations,
                    },
                };
            });
        },
        onError: (error) => {
            console.error("Error details:", error);
        },
    });

    const handleSave = () => {
        updateWebhookMutation.mutate({
            webhookId: localWebhook.id,
            data: {
                url: localWebhook.url,
                event: localWebhook.event,
                event_filters: localWebhook.event_filters,
                status: localWebhook.status,
            },
        });
    };

    return (
        <div className="flex-col " key={webhook?.id}>
            <div className="flex flex-col px-4 py-4" key={webhook.url}>
                <div className="flex items-start justify-between w-full">
                    <div className="flex items-center">
                        <div className="flex flex-col max-w-[450px] text-truncate gap-1 mb-4">
                            <Label className="truncate ">{webhook.url}</Label>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 ">
                        {localWebhook.status === "active" ? (
                            <Label className=" text-xs font-normal  text-[#2563EB]">
                                Webhook ON
                            </Label>
                        ) : (
                            <Label className=" font-normal text-xs text-[#8590A2]">
                                Webhook OFF
                            </Label>
                        )}

                        <Switch
                            checked={
                                localWebhook.status === "active" ? true : false
                            }
                            onCheckedChange={(checked) =>
                                setLocalWebhook((prev) => ({
                                    ...prev,
                                    status: checked ? "active" : "pause",
                                }))
                            }
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <Input
                        className="w-full h-9 focus:border-[#e3e3e3] border-[#E4E4E7] focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 "
                        value={localWebhook.url}
                        onChange={(e) =>
                            setLocalWebhook((prev) => ({
                                ...prev,
                                url: e.target.value,
                            }))
                        }
                        placeholder="https://example.com/webhook"
                        type="url"
                        pattern="https://.*"
                        title="Please enter a valid URL starting with https://"
                    />
                    <Button
                        variant={"outline"}
                        className="flex items-center gap-2 text-xs font-normal h-9 "
                        onClick={() =>
                            handleOpenDeliveries(
                                localWebhook.id,
                                localWebhook.url,
                            )
                        }
                    >
                        <PackageSearch className=" text-[#191B33]" size={16} />
                        View Deliveries
                    </Button>
                    <Button
                        variant={"outline"}
                        className="p-2.5 h-9 w-9"
                        onClick={handleSave}
                        disabled={webhook === localWebhook}
                    >
                        <Save className="text-[#191B33]" size={16} />
                    </Button>
                    <Button
                        variant={"outline"}
                        className="p-2.5 h-9 w-9"
                        onClick={() => handleDeleteWebhook(localWebhook.id)}
                    >
                        <Trash className=" text-[#191B33]" size={16} />
                    </Button>
                </div>
                <Accordion type="single" collapsible>
                    <AccordionItem
                        className="border-none "
                        value="advanced-settings"
                    >
                        <AccordionTrigger className="hover:no-underline pb-0  max-w-[130px]">
                            <span className="text-xs font-normal">
                                Advanced Settings
                            </span>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="flex flex-col gap-1 p-2">
                                <Label className="text-xs text-[#44546F]">
                                    Triggers
                                </Label>
                                <p className="text-xs text-[#8590A2]">
                                    Trigger a notification upon a completed form
                                    submission, or a partial submission if
                                    you’ve added a partial submit point to the
                                    form.
                                </p>
                                <Select
                                    value={
                                        localWebhook.event_filters?.includes(
                                            "partial",
                                        ) &&
                                        localWebhook.event_filters?.includes(
                                            "completed",
                                        )
                                            ? "all"
                                            : localWebhook.event_filters?.includes(
                                                  "completed",
                                              )
                                            ? "completed"
                                            : localWebhook.event_filters?.includes(
                                                  "partial",
                                              )
                                            ? "partial"
                                            : ""
                                    }
                                    onValueChange={(val) => {
                                        let filters: string[] = [];
                                        if (val === "all")
                                            filters = ["partial", "completed"];
                                        else if (val === "completed")
                                            filters = ["completed"];
                                        else if (val === "partial")
                                            filters = ["partial"];
                                        setLocalWebhook((prev) => ({
                                            ...prev,
                                            event_filters: filters,
                                        }));
                                    }}
                                >
                                    <SelectTrigger className="w-full h-8">
                                        <SelectValue placeholder="Select trigger" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="all">
                                                All
                                            </SelectItem>
                                            <SelectItem value="completed">
                                                Completed
                                            </SelectItem>
                                            <SelectItem value="partial">
                                                Partial
                                            </SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            <Separator />
        </div>
    );
};
